import { Formik, FormikValues } from 'formik';
import React, { Fragment, useState, useContext, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import axios from 'axios';
import { toast } from 'react-toastify';

import { MessageContext } from '../../contexts/messageContext';
import FormField from '../FormField';
import { PRESIGN_LINK } from '../../graphql/PRESIGN_LINK';
import { SAVE_PRESIGNED_LINK } from '../../graphql/SAVE_PRESIGNED_LINK';
import { EUploadTypes, formatFilename } from '../Upload';
import { ReactComponent as SendIcon } from '../../assets/svg/send-icon-white-v2.svg';
import { ReactComponent as EmojiIcon } from '../../assets/svg/smile-face.svg';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { Popover } from 'react-tiny-popover';

const MessageReplyForm = () => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { actions, currentRoom } = useContext(MessageContext);
  const [, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadedImageId, setUploadedImageId] = useState<number | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [preSignLink] = useMutation(PRESIGN_LINK);
  const [savePreSignedLink] = useMutation(SAVE_PRESIGNED_LINK);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const textarea = document.querySelector('.page-messages-column-footer-form-textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const uploadToS3 = async (file: File, signedRequest: string) => {
    const options = {
      headers: {
        'Content-Type': file.type
      }
    };
    await axios.put(signedRequest, file, options);
  };

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a PNG or JPG image');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setIsUploadingImage(true);
    try {
      const signedLinkResponse = await preSignLink({
        variables: {
          fileName: formatFilename(file.name),
          fileType: file.type,
          uploadType: EUploadTypes.message
        }
      });

      const { signedRequest, url } = signedLinkResponse.data.preSignLink;
      await uploadToS3(file, signedRequest);

      const savedMedia = await savePreSignedLink({
        variables: {
          fileUrl: url,
          uploadType: EUploadTypes.message
        }
      });

      setUploadedImageId(savedMedia.data.savePreSignedLink.id);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      setSelectedImage(null);
      setImagePreview(null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setUploadedImageId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderForm = (formikData: FormikValues) => {
    const {
      values,
      handleChange,
      handleBlur,
      handleSubmit,
      isSubmitting,
      dirty
    } = formikData;

    return (
      <form
        onSubmit={handleSubmit}
        className="page-messages-column-footer-form"
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {imagePreview && (
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #e0e0e0' }}
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', width: '100%' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            <div style={{ position: 'relative', flex: 1 }}>
              <FormField
                name="reply"
                type="text"
                placeholder="Type a message..."
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.reply}
                multiline={true}
                autoResize={true}
                additionalInputClasses="page-messages-column-footer-form-textarea has-background-white"
                additionalFieldClasses={['message-reply-field']}
                disabled={isSubmitting || isUploadingImage}
                inputRef={textareaRef}
              />
              <div
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2
                }}
              >
                <Popover
                  isOpen={showEmojiPicker}
                  positions={['top', 'left']}
                  padding={10}
                  onClickOutside={() => setShowEmojiPicker(false)}
                  content={
                    <EmojiPicker
                      onEmojiClick={(emoji: EmojiClickData) => {
                        const currentValue = values.reply;
                        const newValue = currentValue + emoji.emoji;
                        handleChange({ target: { name: 'reply', value: newValue } } as any);
                        setShowEmojiPicker(false);
                      }}
                      autoFocusSearch={false}
                      searchDisabled={true}
                      skinTonesDisabled={true}
                      previewConfig={{
                        defaultCaption: '',
                        showPreview: false
                      }}
                      width={300}
                      height={400}
                    />
                  }
                >
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    disabled={isSubmitting || isUploadingImage}
                  >
                    <EmojiIcon style={{ width: '24px', height: '24px', opacity: 0.6 }} />
                  </button>
                </Popover>
              </div>
            </div>
            <button
              type="submit"
              disabled={(!dirty && !uploadedImageId) || isSubmitting || isUploadingImage}
              style={{
                background: '#5775FF',
                border: 'none',
                borderRadius: '50%',
                cursor: (!dirty && !uploadedImageId) || isSubmitting || isUploadingImage ? 'not-allowed' : 'pointer',
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                minWidth: '44px',
                minHeight: '44px',
                marginBottom: '2px'
              }}
            >
              <SendIcon
                width="20"
                height="20"
                style={{
                  fill: (dirty || uploadedImageId) && !isSubmitting && !isUploadingImage ? 'white' : '#9ca3af'
                }}
              />
            </button>
          </div>
        </div>
      </form>
    );
  };

  return (
    <Fragment>
      <footer className="card-footer page-messages-column-card-footer is-block is-absolute">
        <Formik
          initialValues={{ reply: '' }}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            if (values.reply === '' && !uploadedImageId) {
              return;
            }

            setSubmitting(true);

            try {
              await actions.handleCreateMessage(
                currentRoom.data.getRoom.id,
                values.reply || '',
                uploadedImageId ?? undefined
              );
              resetForm();
              handleRemoveImage();
            } catch (error) {
              console.error('Error creating message:', error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {renderForm}
        </Formik>
      </footer>
    </Fragment>
  );
};

export default MessageReplyForm;
